import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Songbook, db, Song, SongMedia, SongMediaAudio, SongMediaColumnWidth } from '@/core/db.ts';
import randId from '@/core/id.ts';

/* * * * * * *
 *  SONGBOOK *
 * * * * * * */

export function useSongbook(id: string) {
  return useQuery<Songbook>({
    queryKey: ['songbooks', id],
    queryFn: () => db.songbooks.get(id).then(async (r) => {
      if (!r) {
        throw new Error('Not found.');
      }

      await db.songbooks.update(id, { lastUsed: new Date() });

      return r;
    }),
    retry: false,
  });
}

export function useAddSongbook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['addSongbook'],
    mutationFn: async ({ title, color }: Pick<Songbook, 'title' | 'color'>) => {
      const now = new Date();
      const id = randId();

      await db.songbooks.add({
        id,
        title,
        color,
        createdAt: now,
        lastUsed: now,
        updatedAt: now,
      });
      await queryClient.invalidateQueries({ queryKey: ['songbooks'] });
    },
  });
}

export function useEditSongbook(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['editSongbook', id],
    mutationFn: async ({ title, color }: Pick<Songbook, 'title' | 'color'>) => {
      const now = new Date();

      await db.songbooks.update(
        id,
        {
          title,
          color,
          updatedAt: now,
        },
      );
      await queryClient.invalidateQueries({ queryKey: ['songbooks'] });
    },
  });
}

export function useRemoveSongbook(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['removeSongbook', id],
    mutationFn: async () => {
      const songs = await db.songs.where({ songbookId: id }).toArray();

      await Promise.all(songs.map((s) => removeSongAndMedia(s.id)));

      await db.songbooks.delete(id);

      await queryClient.invalidateQueries({ queryKey: ['songbooks'] });
      await queryClient.invalidateQueries({ queryKey: ['songs'] });
      await queryClient.invalidateQueries({ queryKey: ['song'] });
    },
  });
}

export function useSongbooks() {
  return useQuery<Songbook[]>({
    queryKey: ['songbooks'],
    queryFn: () => db.songbooks
      .orderBy('lastUsed')
      .reverse()
      .toArray(),
  });
}

/* * * * *
 *  SONG *
 * * * * */

export function useSong(id: string) {
  return useQuery<Song>({
    queryKey: ['song', id],
    queryFn: () => db.songs.get(id).then(async (r) => {
      if (!r) {
        throw new Error('Not found.');
      }

      await db.songs.update(id, { lastUsed: new Date() });

      return populateDefaultsSong(r);
    }),
    retry: false,
  });
}

export function useAddSong(songbookId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['addSong', songbookId],
    mutationFn: async () => {
      const id = randId();
      const now = new Date();

      const currentSongs = await db.songs.where({ songbookId }).count();

      await db.songs.add({
        id,
        songbookId,
        title: `Song (${currentSongs + 1})`,
        orientation: 'horizontal',
        order: currentSongs + 1,
        createdAt: now,
        lastUsed: now,
        updatedAt: now,
      });
      await queryClient.invalidateQueries({ queryKey: ['songs'] });

      return id;
    },
  });
}

export function useEditSong(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['editSong', id],
    mutationFn: async (newProps: Partial<Song>) => {
      const now = new Date();

      await db.songs.update(
        id,
        {
          ...newProps,
          updatedAt: now,
        },
      );
      await queryClient.invalidateQueries({ queryKey: ['songs'] });
      await queryClient.invalidateQueries({ queryKey: ['song', id] });
    },
  });
}

function reOrderSongs(songs: Song[]) {
  return db.songs.bulkUpdate(songs.map((s, index) => ({
    key: s.id,
    changes: {
      order: index + 1,
    },
  })));
}

export function useRemoveSong(song: Song) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['removeSong', song.id],
    mutationFn: async () => {
      await removeSongAndMedia(song.id);

      const songs = await db.songs
        .where({ songbookId: song.songbookId })
        .sortBy('order');

      await reOrderSongs(songs);

      await queryClient.invalidateQueries({ queryKey: ['songs'] });
      await queryClient.invalidateQueries({ queryKey: ['song'] });
    },
  });
}

export function useOrderSongs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['orderSongs'],
    mutationFn: async (songs: Song[]) => {
      await reOrderSongs(songs);

      await queryClient.invalidateQueries({ queryKey: ['songs'] });
      await queryClient.invalidateQueries({ queryKey: ['song'] });
    },
  });
}

export function useSongs(songbookId: string) {
  return useQuery<Song[]>({
    queryKey: ['songs', songbookId],
    queryFn: () => db.songs
      .where({ songbookId })
      .sortBy('order')
      .then((songs) => songs.map(populateDefaultsSong)),
  });
}

/* * * * * * * *
 *  SONG MEDIA *
 * * * * * * * */

function reOrderSongMedia(media: SongMedia[]) {
  return db.media.bulkUpdate(media.map((s, index) => ({
    key: s.id,
    changes: {
      order: index + 1,
    },
  })));
}

export function useSongMedia(songId: string) {
  return useQuery<SongMedia[]>({
    queryKey: ['songMedia', songId],
    queryFn: () => db.media
      .where({ songId })
      .sortBy('order'),
    retry: false,
  });
}

export function useAddSongMedia(songId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['addSongMedia', songId],
    mutationFn: async (props: Pick<SongMedia, 'type' | 'contents'>) => {
      const id = randId();

      const currentMedia = await db.media.where({ songId }).count();

      await db.media.add(
        {
          id,
          songId,
          order: currentMedia + 1,
          width: 'auto',
          ...props,
        },
      );
      await queryClient.invalidateQueries({ queryKey: ['songMedia', songId] });
    },
  });
}

export function useUpdateSongMediaOptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateSongMediaOptions'],
    mutationFn: async ({ id, options }: { id: string, options: SongMedia['options'] }) => {
      await db.media.update(id, { options });
      await queryClient.invalidateQueries({ queryKey: ['songMedia'] });
    },
  });
}

export function useUpdateSongMediaWidth(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['useUpdateSongMediaWidth', id],
    mutationFn: async (width: SongMediaColumnWidth) => {
      await db.media.update(id, { width });
      await queryClient.invalidateQueries({ queryKey: ['songMedia'] });
    },
  });
}

export function useRemoveSongMedia(song: Song) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['removeSongMedia', song.id],
    mutationFn: async (id: string) => {
      await db.media.delete(id);

      const media = await db.media
        .where({ songId: song.id })
        .sortBy('order');

      await reOrderSongMedia(media);

      await queryClient.invalidateQueries({ queryKey: ['songMedia'] });
    },
  });
}

export function useOrderSongMedia(song: Song) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['orderSongMedia', song.id],
    mutationFn: async ({ id, direction }: { id: string; direction: 'up' | 'down' }) => {
      const media = await db.media
        .where({ songId: song.id })
        .sortBy('order');

      const i = media.findIndex((m) => m.id === id);
      const j = i + (direction === 'up' ? 1 : -1);

      [media[i], media[j]] = [media[j], media[i]];

      await reOrderSongMedia(media);

      await queryClient.invalidateQueries({ queryKey: ['songMedia'] });
    },
  });
}

/* * * * * * *
 * UTILITIES *
 * * * * * * */

export function populateDefaultsSong(song: Song) {
  const orientation = song.orientation || 'horizontal';

  return {
    ...song,
    orientation,
  };
}

export function useInvalidateAll() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['songbooks'] }),
      queryClient.invalidateQueries({ queryKey: ['songs'] }),
      queryClient.invalidateQueries({ queryKey: ['song'] }),
      queryClient.invalidateQueries({ queryKey: ['songMedia'] }),
    ]);
  }, [queryClient]);
}

export function useDecodeAudio(mediaFile: SongMediaAudio) {
  return useQuery<AudioBuffer>({
    queryKey: ['decodedAudio', mediaFile],
    queryFn: () => {
      return new window.AudioContext().decodeAudioData(mediaFile.contents);
    },
    retry: false,
  });
}

async function removeSongAndMedia(id: string) {
  const media = await db.media.where({ songId: id }).toArray();

  await Promise.all(media.map((m) => db.media.delete(m.id)));

  await db.songs.delete(id);
}
